import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiWifi, FiWifiOff } from 'react-icons/fi';

const ConnectionStatus = ({ lastUpdate, isRefreshing = false }) => {
    const { t } = useTranslation();
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [lastSeen, setLastSeen] = useState(null);

    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    useEffect(() => {
        if (lastUpdate) {
            setLastSeen(lastUpdate);
        }
    }, [lastUpdate]);

    // Determine connection status
    const getStatus = () => {
        if (!isOnline) return 'offline';
        if (isRefreshing) return 'updating';
        if (!lastSeen) return 'unknown';

        const timeSinceUpdate = Date.now() - lastSeen.getTime();
        if (timeSinceUpdate < 30000) return 'connected'; // Less than 30 seconds
        if (timeSinceUpdate < 120000) return 'slow'; // Less than 2 minutes
        return 'stale'; // More than 2 minutes
    };

    const status = getStatus();

    const getStatusColor = () => {
        switch (status) {
            case 'connected':
            case 'updating':
                return 'text-green-500';
            case 'slow':
                return 'text-yellow-500';
            case 'offline':
            case 'stale':
                return 'text-red-500';
            default:
                return 'text-gray-400';
        }
    };

    const getStatusText = () => {
        switch (status) {
            case 'connected':
                return t('Connected');
            case 'updating':
                return t('Updating...');
            case 'slow':
                return t('Slow connection');
            case 'offline':
                return t('No internet');
            case 'stale':
                return t('Connection lost');
            default:
                return t('Unknown');
        }
    };

    if (status === 'connected' && !isRefreshing) {
        return null; // Don't show when everything is fine
    }

    return (
        <div className="flex items-center space-x-1 text-xs">
            {isOnline ? (
                <FiWifi className={`w-3 h-3 ${getStatusColor()} ${isRefreshing ? 'animate-pulse' : ''}`} />
            ) : (
                <FiWifiOff className={`w-3 h-3 ${getStatusColor()}`} />
            )}
            <span className={`${getStatusColor()} font-medium`}>
                {getStatusText()}
            </span>
        </div>
    );
};

export default ConnectionStatus;
